import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useRef } from 'react';
import { PolicyAction, PolicyFormValues } from '../policy-dialog';
import OrganizationsAutoComplete from '../../../shared/organizations-autocomplete';
import z from 'zod';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

interface SetOrganizationActionProps {
  setValue: UseFormSetValue<PolicyFormValues>;
  control: Control<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
}

export const SetOrganizationActionSchema = z.object({
  type: z.literal('Set_Organization'),
  orgUnit: z.string().min(1, 'A org unit is required.'),
});

export type SetOrganizationActionType = z.infer<
  typeof SetOrganizationActionSchema
>;

// Type guard to check if an action is SetOrganizationActionType
function isSetOrganizationAction(
  action: PolicyAction
): action is SetOrganizationActionType {
  return action.type === 'Set_Organization';
}

export default function SetOrganizationAction({
  setValue,
  watch,
  errors,
  control,
}: SetOrganizationActionProps) {
  const autocompleteKey = useRef(new Date().getTime().toString());
  const actionData = watch('actionData');
  const idx = actionData
    .map((action) => action.type)
    .indexOf('Set_Organization');
  const actionAtIndex = actionData[idx];
  if (!isSetOrganizationAction(actionAtIndex)) {
    return null;
  }

  const currentAction: SetOrganizationActionType = actionAtIndex;
  const error: FieldErrors<SetOrganizationActionType> | undefined =
    errors?.actionData?.[idx];

  return (
    <Stack sx={{ p: 1 }}>
      <Stack gap={2}>
        <Box sx={{ width: 400 }}>
          <Controller
            name={`actionData.${idx}.orgUnit`}
            control={control}
            render={() => (
              <OrganizationsAutoComplete
                newKey={`${autocompleteKey.current}`}
                autoCompleteValue={currentAction.orgUnit}
                setAutoCompleteValue={(newValue) =>
                  setValue(`actionData.${idx}.orgUnit`, newValue, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                hasError={!!error?.orgUnit}
                helperText={error?.orgUnit?.message || ' '}
              />
            )}
          />
        </Box>
      </Stack>
    </Stack>
  );
}
